import { t } from 'i18next';
import { isNil } from 'lodash';
import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormControlLabel,
  FormGroup,
  Switch,
  ButtonProps,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { CustomModal } from '@components';
import { theme } from '@store';

import {
  DatepickerFormField,
  NumberInputFormField,
  SelectFormField,
  TextAreaFormField,
} from 'components/DynamicFormFields';
import { useAddOutreachAttemptModal } from './hooks/useAddOutreachAttemptModal';
import dayjs from 'dayjs';
import { MAXIMUM_TEXT_AREA_LENGTH } from '@common/types/constants';

interface IAddOutreachAttemptModalProps {
  task_id: string;
  disabled?: boolean;
  buttonProps?: ButtonProps;
  buttonText?: string;
}

export const AddOutreachAttemptModal = ({
  task_id,
  disabled = false,
  buttonProps,
  buttonText = t('worklist.addOutreachAttempt.openButtonText'),
}: IAddOutreachAttemptModalProps) => {
  const {
    formMethods,
    onSubmit,
    loading,
    isOpen,
    handleOpen,
    handleClose,
    outreachDispositionOptions,
    outreachMethodOptions,
    submitDateValue,
  } = useAddOutreachAttemptModal(task_id);

  const {
    control,
    formState: { isSubmitting, isValid },
    setValue,
    trigger,
  } = formMethods;

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        sx={{
          fontWeight: 'regular',
          fontSize: '1rem',
          width: 'fit-content',
        }}
        variant={'text'}
        onClick={handleOpen}
        disabled={disabled}
        {...buttonProps}
      >
        {buttonText}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        isLoading={loading}
        title={`${t('worklist.addOutreachAttempt.title')}`}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.7rem',
            }}
          >
            <DatepickerFormField
              name='attempt_date'
              label={t('worklist.addOutreachAttempt.attemptDate')}
              optional={false}
              disableFuture={true}
              disablePast={false}
              onChangeCallback={() => {
                setValue('new_latest_date', '');
                trigger();
              }}
            />

            <SelectFormField
              name='outreach_method'
              label='Outreach method'
              options={outreachMethodOptions}
            />

            <SelectFormField
              name='outreach_disposition'
              label='Disposition'
              options={outreachDispositionOptions}
            />

            <DatepickerFormField
              name='new_latest_date'
              label={t('worklist.addOutreachAttempt.newLatestDate')}
              optional={false}
              disableFuture={false}
              disablePast={true}
              minDate={dayjs(new Date())}
              maxDate={dayjs(submitDateValue).add(7, 'day')}
            />

            <TextAreaFormField
              optional
              maxRows={4}
              name='notes'
              required={true}
              label={t('worklist.addOutreachAttempt.notes')}
              charLimit={MAXIMUM_TEXT_AREA_LENGTH}
            />

            <NumberInputFormField
              name='recorded_minutes'
              label={t('worklist.addOutreachAttempt.minutes')}
              optional={false}
            />

            <Controller
              name='notable_call'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth>
                  <FormGroup>
                    <FormControlLabel
                      sx={{ width: 'fit-content' }}
                      control={<Switch checked={field.value} {...field} />}
                      label={t('worklist.addOutreachAttempt.notable')}
                    />
                  </FormGroup>
                  <FormHelperText error={!isNil(error)}>
                    {error ? error.message : null}
                  </FormHelperText>
                </FormControl>
              )}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Typography
                variant='caption'
                sx={{ color: theme.palette.semiBlack.main }}
              >
                The attempt will be saved as a care note in patient details.
              </Typography>
              <Box>
                <Button
                  variant='text'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                  }}
                  disabled={isSubmitting}
                  onClick={handleClose}
                >
                  {t('general.cancel')}
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  color='secondary'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                    color: 'white',
                  }}
                  loading={loading}
                  disabled={!isValid || loading}
                >
                  {t('worklist.addOutreachAttempt.openButtonText')}
                </Button>
              </Box>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
